import { useState } from 'react';
import { Box, Stack, Typography, IconButton, Button, CircularProgress, Alert, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from 'react-router-dom';

import CardShell from '../components/CardShell';
import { HeaderPill, ColHeader } from './ClosurePayrollDataPage';
import { useClosureAccountingData } from '../hooks/useClosureAccountingData';
import { updateClosureEmployee, updatePayrollClosure } from '../api/liquidacionApi';
import { mapApiError } from '../../../../shared/utils/mapApiError';
import { fmtDMY ,ymdToDate} from '../../../../shared/utils/dateFormat';
import { formatMoney } from '../utils/moneyFormat';

import ClosureEmployeeAccountingModal from '../components/ClosureEmployeeAccountingModal';

function formatDateRange(start, end) {
    if (!start || !end) return '';
    const parse = (v) => v instanceof Date ? v : ymdToDate(String(v).slice(0, 10));
    return `${fmtDMY(parse(start))} - ${fmtDMY(parse(end))}`;
}

const GRID_COLS = 'minmax(120px, 1.1fr) 105px 115px 130px 140px 100px minmax(100px, 1fr) 30px';

function AccountingRow({ employee, closureId, onPatchError, onSilentRefetch, isReady }) {
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const handleSaveAccounting = async (payload) => {
        setSaving(true);
        try {
            await updateClosureEmployee(closureId, employee.employeeId, payload);
            setModalOpen(false);
            onSilentRefetch();
        } catch (err) {
            onPatchError(mapApiError(err).message || 'Error al guardar datos');
        } finally {
            setSaving(false);
        }
    };

    const totalDisplay = formatMoney(employee.totalAmount);
    const totalPositive = Number(employee.totalAmount ?? 0) >= 0;

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLS,
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.2,
                    bgcolor: 'neutral.50',
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'neutral.200',
                    opacity: saving ? 0.7 : 1,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: 'neutral.100' },
                    mb: 1
                }}
            >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {employee.fullName}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.800', textAlign: 'center' }}>
                    {employee.cuitSnapshot || '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                    {employee.bankSnapshot || '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                    {employee.cvuSnapshot || '—'}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center' }}>
                    {employee.accountAliasSnapshot || '—'}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: totalPositive ? 'success.800' : 'error.800', textAlign: 'center' }}>
                    {totalDisplay}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'neutral.700', wordBreak: 'break-word' }}>
                    {employee.observations || '—'}
                </Typography>

                {!isReady && (
                    <Tooltip title="Editar datos bancarios" arrow placement="top">
                        <IconButton
                            onClick={saving ? undefined : () => setModalOpen(true)}
                            size="small"
                            sx={{ bgcolor: 'secondary.100', color: 'primary.700', '&:hover': { bgcolor: 'secondary.200' } }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <ClosureEmployeeAccountingModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                employee={employee}
                onSave={handleSaveAccounting}
                saving={saving}
            />
        </>
    );
}

export default function ClosureAccountingDataPage() {
    const navigate = useNavigate();
    const { closureId } = useParams();

    const { closure, employees, loading, error, refetch } = useClosureAccountingData(closureId);
    const [patchError, setPatchError] = useState('');
    const [savingStep, setSavingStep] = useState(false);

    if (loading || !closure || closure.id !== closureId){
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        );
    }

    const isReady = closure?.status === 'READY';

    const handleBack = () => {
        navigate(`/rrhh/liquidacion/${closureId}/datos`);
    };

    const handleFinish = async () => {
        if (!isReady) {
            setSavingStep(true);
            try {
                await updatePayrollClosure(closureId, { currentStep: 5 });
                navigate(`/rrhh/liquidacion/${closureId}/reporte`);
            } catch (err) {
                setPatchError(mapApiError(err).message);
            } finally {
                setSavingStep(false);
            }
        } else {
            navigate(`/rrhh/liquidacion/${closureId}/reporte`);
        }
    };

    if (error) {
        return (
            <Stack spacing={2} sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={handleBack} sx={{ alignSelf: 'flex-start' }}>Volver</Button>
            </Stack>
        );
    }

    const dateRange = closure ? formatDateRange(closure.periodStart, closure.periodEnd) : '';

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>

            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <IconButton onClick={() => navigate('/rrhh/liquidacion')} sx={{ width: 40, height: 40, bgcolor: 'secondary.500', '&:hover': { bgcolor: 'secondary.600' } }}>
                    <ArrowBackIcon sx={{ color: 'logoblanco', fontSize: 32 }} />
                </IconButton>

                <HeaderPill sx={{ bgcolor: 'secondary.200' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900' }}>
                        {isReady ? `Detalle Liquidacion: ${closure?.name || ''}` : 'Datos de Pago / Contables'}
                    </Typography>
                </HeaderPill>

                {dateRange && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'secondary.50', borderRadius: '20px', border: '1.5px solid', borderColor: 'secondary.200' }}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: 'primary.700' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900' }}>{dateRange}</Typography>
                    </Box>
                )}
            </Stack>
            {patchError && <Alert severity="error" onClose={() => setPatchError('')}>{patchError}</Alert>}

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#A9A9B6", borderRadius: "24px", p: 2, overflow: "hidden" }}>
                <CardShell sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>

                    <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, gap: 1.5, px: 1.5, pb: 1.5, flexShrink: 0 }}>
                        <ColHeader>Nombre y apellido</ColHeader>
                        <ColHeader>CUIT</ColHeader>
                        <ColHeader>Banco</ColHeader>
                        <ColHeader>CVU / CBU</ColHeader>
                        <ColHeader>Alias</ColHeader>
                        <ColHeader>Total</ColHeader>
                        <ColHeader>Observaciones</ColHeader>
                        <Box />
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {employees.length === 0 ? (
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'neutral.700', textAlign: 'center', py: 4 }}>
                                No hay empleados en este cierre
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {employees.map((emp) => (
                                    <AccountingRow
                                        key={emp.employeeId}
                                        employee={emp}
                                        closureId={closureId}
                                        onPatchError={setPatchError}
                                        onSilentRefetch={refetch}
                                        isReady={isReady}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </CardShell>
            </Box>
            <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 2, flexShrink: 0 }}>
                <Button onClick={handleBack} disabled={savingStep} sx={{ bgcolor: "#ffcdd2", color: "primary.900", px: 3, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, '&:hover': { bgcolor: "#ffb3ba" } }}>
                    Atrás
                </Button>
                <Button onClick={handleFinish} disabled={employees.length === 0 || savingStep} sx={{ bgcolor: "#b9f6ca", color: "primary.900", px: 3, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, display: 'flex', gap: 1, '&:hover': { bgcolor: "#a5f5bd" }, '&:disabled': { opacity: 0.6 } }}>
                    {savingStep ? <CircularProgress size={20} color="inherit" /> : 'Continuar'}
                </Button>
            </Stack>
        </Box>
    );
}
